import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { useCanCreateProject } from 'abilities/projects';
import {
  useIsManagedUser,
  useIsWellSaidAdmin,
  useUserContext,
} from 'context/user/UserContext';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import { useFeatureFlags } from 'components/FeatureFlags';
import { useProductUsageQuery } from 'components/ProductUsage/operation.generated';
import useOnProjectCreationSuccess from 'components/Projects/useOnProjectCreationSuccess';
import ProjectCreationModal from 'components/Projects/ProjectCreation/ProjectCreationModal';
import GuidedImportFlow from 'components/Studio/ImportScriptV2/GuidedImportFlow';
import Tooltip from 'components/Tooltip';

interface NavCreateButtonProps {
  isLeftRailOpen: boolean;
}

const useStyles = makeStyles()(theme => ({
  buttonWrapper: {
    display: 'block',
  },
  button: {
    width: '100%',
    minWidth: 0,
    padding: theme.spacing(1),
    justifyContent: 'center',
  },
  label: {
    opacity: 1,
    maxWidth: 100,
    paddingLeft: theme.spacing(1),
    overflow: 'hidden',
    transition: theme.transitions.create(
      ['opacity', 'max-width', 'padding-left'],
      {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shortest,
      }
    ),
    whiteSpace: 'nowrap',
  },
  hideLabel: {
    opacity: 0,
    maxWidth: 0,
    paddingLeft: 0,
  },
}));

const NavCreateButton = ({ isLeftRailOpen }: NavCreateButtonProps) => {
  const { classes, cx } = useStyles();
  const [open, setOpen] = React.useState(false);
  const handleCloseForm = React.useCallback(() => setOpen(false), []);

  const canCreateProject = useCanCreateProject();
  const { data: usageData } = useProductUsageQuery();
  const [, dispatch] = usePromptsContext();
  const isManagedUser = useIsManagedUser();
  const { data } = useUserContext();
  const isExpiredTrial = data?.user.subscription?.status === 'TRIAL_EXPIRED';
  const { isFeatureEnabled } = useFeatureFlags();
  const isWellSaidAdmin = useIsWellSaidAdmin();
  // WellSaid admins keep the version-picker modal so they can still create Legacy projects
  const applyGuidedProjectCreation =
    isFeatureEnabled('guided-project-creation') && !isWellSaidAdmin;
  const onProjectCreationSuccess = useOnProjectCreationSuccess({
    setStorage: true,
  });

  const exceedsProductLimit = useMemo(() => {
    if (!usageData) return false;
    const limit = usageData.user?.productAccessLimits?.projectsLimit;
    const used = usageData.user?.productAccessPeriodUsage?.projects;
    return limit !== -1 && used >= limit;
  }, [usageData]);

  const disabledAction =
    (exceedsProductLimit || isExpiredTrial) && isManagedUser;

  const handleClick = React.useCallback(() => {
    if (exceedsProductLimit && !isManagedUser) {
      dispatch({
        type: 'OpenSubscriptionTiersPrompt',
        payload: {
          upgradeContext: 'access_limit_projects',
          upgradePath: 'Dashboard > Nav > New project',
        },
      });
      return;
    }
    setOpen(true);
  }, [dispatch, exceedsProductLimit, isManagedUser]);

  if (!canCreateProject) return null;

  return (
    <>
      <Tooltip
        text="New project"
        placement="right-end"
        PopperProps={{
          hidden: isLeftRailOpen,
        }}
      >
        <span className={classes.buttonWrapper}>
          <Button
            variant="primary"
            className={classes.button}
            size="small"
            onClick={handleClick}
            disabled={disabledAction}
            aria-label="Create new project"
          >
            <Icon type="plus" />
            <Typography
              variant="h5"
              bold
              color="inherit"
              className={cx(classes.label, {
                [classes.hideLabel]: !isLeftRailOpen,
              })}
            >
              New project
            </Typography>
          </Button>
        </span>
      </Tooltip>
      {applyGuidedProjectCreation && open ? (
        <GuidedImportFlow close={handleCloseForm} />
      ) : (
        /* No driveId passed — backend defaults to the user's personal drive */
        <ProjectCreationModal
          open={open}
          onClose={handleCloseForm}
          onSuccess={projectId => onProjectCreationSuccess({ projectId })}
          source="left_nav"
        />
      )}
    </>
  );
};

export default NavCreateButton;
